import { useState, useEffect } from "react";
import { AppHeader } from "@/components/layout/AppHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  TrendingUp, 
  Calendar,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Download
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { invoiceApi } from "@/services/api";

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

export default function RevenuePage() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [stats, setStats] = useState({
    totalRevenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
    overdueRevenue: 0,
    monthlyData: [],
    statusBreakdown: []
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    if (invoices.length > 0) {
      calculateStats();
    }
  }, [invoices, selectedYear]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await invoiceApi.getInvoices(1, 1000);
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const year = parseInt(selectedYear);
    
    // Filter invoices by selected year
    const yearInvoices = invoices.filter(inv => {
      const invDate = inv.invoiceInfo?.date;
      if (!invDate) return false;
      return new Date(invDate).getFullYear() === year;
    });

    // Calculate totals
    let totalRevenue = 0;
    let paidRevenue = 0;
    let pendingRevenue = 0;
    let overdueRevenue = 0;

    // Monthly breakdown
    const monthlyTotals = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    months.forEach(m => {
      monthlyTotals[m] = { paid: 0, pending: 0, overdue: 0 };
    });

    yearInvoices.forEach(inv => {
      const items = inv.lineItems || [];
      const subtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
      const total = subtotal * 1.13; // With HST
      const status = inv.invoiceInfo?.status || 'pending';
      const invDate = new Date(inv.invoiceInfo?.date);
      const monthName = months[invDate.getMonth()];

      totalRevenue += total;

      if (status === 'paid') {
        paidRevenue += total;
        monthlyTotals[monthName].paid += total;
      } else if (status === 'pending') {
        pendingRevenue += total;
        monthlyTotals[monthName].pending += total;
      } else if (status === 'overdue') {
        overdueRevenue += total;
        monthlyTotals[monthName].overdue += total;
      }
    });

    // Format monthly data for chart
    const monthlyData = months.map(month => ({
      month,
      paid: Math.round(monthlyTotals[month].paid * 100) / 100,
      pending: Math.round(monthlyTotals[month].pending * 100) / 100,
      overdue: Math.round(monthlyTotals[month].overdue * 100) / 100,
      total: Math.round((monthlyTotals[month].paid + monthlyTotals[month].pending + monthlyTotals[month].overdue) * 100) / 100
    }));

    // Status breakdown for pie chart
    const statusBreakdown = [
      { name: 'Paid', value: paidRevenue, color: '#10b981' },
      { name: 'Pending', value: pendingRevenue, color: '#f59e0b' },
      { name: 'Overdue', value: overdueRevenue, color: '#ef4444' }
    ].filter(item => item.value > 0);

    setStats({
      totalRevenue,
      paidRevenue,
      pendingRevenue,
      overdueRevenue,
      monthlyData,
      statusBreakdown
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      years.push(y.toString());
    }
    return years;
  };

  // Get recent paid invoices
  const recentPaidInvoices = invoices
    .filter(inv => inv.invoiceInfo?.status === 'paid')
    .sort((a, b) => new Date(b.invoiceInfo?.date) - new Date(a.invoiceInfo?.date))
    .slice(0, 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Revenue</h1>
            <p className="text-muted-foreground mt-1">Track your income and payment status</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-slate-500/10 to-slate-600/5 border-slate-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Total Revenue</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {formatCurrency(stats.totalRevenue)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{selectedYear}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-slate-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-slate-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Paid</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {formatCurrency(stats.paidRevenue)}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" />
                    Collected
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Pending</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">
                    {formatCurrency(stats.pendingRevenue)}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">Awaiting payment</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Overdue</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">
                    {formatCurrency(stats.overdueRevenue)}
                  </p>
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <ArrowDownRight className="w-3 h-3" />
                    Past due date
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Monthly Revenue Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Monthly Revenue - {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis className="text-xs" tickFormatter={(value) => `$${value}`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="paid" name="Paid" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pending" name="Pending" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="overdue" name="Overdue" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status Breakdown Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Payment Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {stats.statusBreakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.statusBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {stats.statusBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No data for {selectedYear}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {stats.statusBreakdown.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-muted-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Paid Invoices */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Recent Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentPaidInvoices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentPaidInvoices.map((invoice) => {
                    const items = invoice.lineItems || [];
                    const subtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
                    const total = subtotal * 1.13;
                    
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.id}</TableCell>
                        <TableCell>{invoice.customerInfo?.name || 'Unknown'}</TableCell>
                        <TableCell>{invoice.invoiceInfo?.date || '-'}</TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">
                          {formatCurrency(total)}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
                            Paid
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No paid invoices yet
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
